/**
 * @file ISBN や商品ページの URL から書誌 (正式な書名・出版社) を引く。
 *
 * @what: 入力 (ISBN-10 / ISBN-13 / Amazon の `/dp/<ISBN-10>` / ISBN-13 を含む URL) を ISBN-13 に
 *   正規化し、openBD (国内の書誌、鍵不要・CORS 可) を直接引いて書名・出版社を返す。
 *   openBD の書名は図書館表記 (「はじめてのめいろ : 2・3・4歳. 1集」) なので、画面用に
 *   「はじめてのめいろ 1集」の形へ整える (displayTitle)。
 * @why: 教材は呼び名でなく正式な書名で登録する (代表 2026-08-23)。URL を貼るだけで書名が
 *   入るようにして、手入力の揺れを無くす。
 * @failure: ISBN が取り出せない → parseIsbn は null。未収録 → lookupBook は null。
 *   通信失敗・HTTP エラーは throw (呼び出し側で文言にする)。
 */

import { isbn13To10 } from "./bookCover";

export const OPENBD_ENDPOINT = "https://api.openbd.jp/v1/get";

const AMAZON_ASIN = /\/(?:dp|gp\/product|ASIN)\/([0-9]{9}[0-9Xx])(?:[/?]|$)/;
const ISBN13_IN_URL = /(97[89][0-9]{10})/;

function isbn13CheckDigit(first12: string): string {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += Number(first12[i]) * (i % 2 === 1 ? 3 : 1);
  }
  return String((10 - (sum % 10)) % 10);
}

/** ISBN-10 / ISBN-13 (ハイフン・空白入り可) を検査数字つきで ISBN-13 に正規化する。 */
function normalizeIsbn(raw: string): string | null {
  const digits = raw.replace(/[-\s]/g, "").toUpperCase();
  if (digits.length === 10) {
    if (!/^[0-9]{9}[0-9X]$/.test(digits)) return null;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += Number(digits[i]) * (10 - i);
    const check = digits[9] === "X" ? 10 : Number(digits[9]);
    if ((sum + check) % 11 !== 0) return null;
    const core = `978${digits.slice(0, 9)}`;
    return core + isbn13CheckDigit(core);
  }
  if (digits.length === 13) {
    if (!/^[0-9]{13}$/.test(digits)) return null;
    if (isbn13CheckDigit(digits.slice(0, 12)) !== digits[12]) return null;
    return digits;
  }
  return null;
}

/**
 * 入力から ISBN-13 を取り出す。
 * @what: URL なら Amazon の `/dp/<ISBN-10>` か、パスに含まれる 13 桁を見る。URL でなければ
 *   ISBN そのものとして扱う。検査数字が合わないものは受け付けない。
 */
export function parseIsbn(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) {
    let path = s;
    try {
      const u = new URL(s);
      path = `${u.pathname}?${u.search}`;
    } catch {
      return null;
    }
    const asin = AMAZON_ASIN.exec(path);
    if (asin) return normalizeIsbn(asin[1]!);
    const in13 = ISBN13_IN_URL.exec(path);
    if (in13) return normalizeIsbn(in13[1]!);
    return null;
  }
  return normalizeIsbn(s);
}

/** 入力が商品ページの URL (https) なら、保存用に整えた URL を返す。それ以外は null。 */
export function productUrl(input: string): string | null {
  const s = input.trim();
  if (!/^https:\/\//i.test(s)) return null;
  try {
    const u = new URL(s);
    // Amazon はトラッキング用のクエリを落とし、/dp/<ASIN> までに丸める
    if (/(^|\.)amazon\./i.test(u.hostname)) {
      const asin = AMAZON_ASIN.exec(u.pathname);
      if (asin) return `${u.origin}/dp/${asin[1]!.toUpperCase()}`;
    }
    return u.toString();
  } catch {
    return null;
  }
}

/** 商品ページを引くときの店である。書影と同じ店に揃える。 */
export const BOOK_STORE_ORIGIN = "https://www.amazon.co.jp";

/**
 * ISBN から商品ページの URL を作る。
 *
 * @what: ISBN-13 を10桁に直して商品ページを指す。10桁に直せない ISBN (接頭が979のもの) は、
 *   ISBN で検索する道を返す。
 * @why: カリキュラムの本は書誌 (ISBN) で持ち、商品ページの URL までは持たないことがある。
 *   持っていないだけで買いに行けなくなるのを防ぐ。店は書影と同じところに揃える
 *   (bookCover.ts と同じ理由で、ISBN 指定の書影がいちばん揃うためである)。
 * @failure: ISBN が無い、または桁が合わないときは null (書名は文字のままになる)。
 */
export function isbnProductUrl(isbn: string | undefined): string | null {
  if (!isbn) return null;
  const digits = isbn.replace(/[-\s]/g, "").toUpperCase();
  if (!/^([0-9]{13}|[0-9]{9}[0-9X])$/.test(digits)) return null;
  const isbn10 = digits.length === 10 ? digits : isbn13To10(digits);
  if (isbn10) return `${BOOK_STORE_ORIGIN}/dp/${isbn10}`;
  return `${BOOK_STORE_ORIGIN}/s?k=${encodeURIComponent(digits)}`;
}

export interface BookMeta {
  isbn: string;
  /** 画面用に整えた書名 (「はじめてのめいろ 1集」)。 */
  title: string;
  /** openBD の書名そのまま (図書館表記)。 */
  rawTitle: string;
  publisher: string;
  series?: string;
  volume?: string;
}

/**
 * openBD の図書館表記を画面用の書名に整える。
 * 「はじめてのめいろ : 2・3・4歳. 1集」 → 「はじめてのめいろ 1集」。副題のうち巻次
 * (「1集」「上」「第2巻」) だけを残し、対象年齢などは落とす。副題が無ければそのまま。
 */
export function displayTitle(raw: string): string {
  const [main, ...rest] = raw.split(" : ");
  const sub = rest.join(" : ").trim();
  const base = (main ?? raw).trim();
  if (!sub) return base;
  const volumes = sub
    .split(/\.\s+/)
    .map((p) => p.trim())
    .filter((p) => /^(?:第?[0-9０-９]+\s*(?:集|巻|冊|号|年生)?|[上中下])$/.test(p));
  return [base, ...volumes].join(" ");
}

interface OpenBdEntry {
  summary?: {
    isbn?: string;
    title?: string;
    volume?: string;
    series?: string;
    publisher?: string;
  };
}

export async function lookupBook(isbn13: string, signal?: AbortSignal): Promise<BookMeta | null> {
  const res = await fetch(`${OPENBD_ENDPOINT}?isbn=${encodeURIComponent(isbn13)}`, {
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) throw new Error(`openBD: HTTP ${res.status}`);
  const entries = (await res.json()) as (OpenBdEntry | null)[];
  const summary = entries[0]?.summary;
  if (!summary?.title) return null;
  const meta: BookMeta = {
    isbn: summary.isbn || isbn13,
    title: displayTitle(summary.title),
    rawTitle: summary.title,
    publisher: summary.publisher ?? "",
  };
  if (summary.series) meta.series = summary.series;
  if (summary.volume) meta.volume = summary.volume;
  return meta;
}
